import { axios } from 'configs';
import type {
  TGetLineFriendsResponse,
  TLineChannelAccount,
  TLineChannelDetail,
  TLineWebhookVerifyResponse,
  TLineFriendDetail,
  TListLineFriendsRequest,
  TLineChannelOperationResponse,
  TSendMigrationMessageResponse,
  TUpdateLineChannelRequest,
  TUpdateLineFriendRequest,
  TCrawlStartResponse,
  TCrawlProgressResponse,
  TCrawlMigrateResponse,
  TCrawlResultItem,
  TMigrateFromCsvEntry,
  TChannelCrawlResult,
  TBulkRegisterChannelEntry,
  TBulkRegisterChannelsResponse,
  TBulkChannelOperationResponse,
} from 'types';
import { ELineChannelStatus } from 'types';

export type RegisterLineChannelRequest = {
  basicId: string;
  channelId: string;
  channelSecret: string;
};

export type ReissueLineChannelAccessTokenRequest = {
  channelSecret: string;
};

export type ReissueLineChannelAccessTokenResponse = {
  accessToken: string;
};

function orgQuery(organizationId?: number): string {
  return organizationId != null ? `?organizationId=${organizationId}` : '';
}

/**
 * 連携済みLINEアカウント一覧取得
 */
export const listLineChannels = async (organizationId?: number) => {
  const response = await axios.get<TLineChannelAccount[]>(`/line/accounts${orgQuery(organizationId)}`);
  return response.data;
};

/**
 * LINE友だち一覧取得
 */
export const listLineFriends = async (
  params: TListLineFriendsRequest,
) => {
  const response = await axios.get<TGetLineFriendsResponse>('/line/friends', {
    params,
  });
  return response.data;
};

/**
 * 連携済みLINEアカウント詳細取得
 */
export const getLineChannel = async (lineChannelId: number) => {
  const response = await axios.get<TLineChannelDetail>(
    `/line/channels/${lineChannelId}`,
  );
  return response.data;
};

/**
 * LINEアカウントの連携
 */
export const registerLineChannel = async (
  request: RegisterLineChannelRequest,
) => {
  const response = await axios.post<TLineChannelOperationResponse>('/line/channels', request);
  return response.data;
};

/**
 * LINEアクセストークンの再発行
 */
export const reissueLineChannelAccessToken = async (
  lineChannelId: number,
  request: ReissueLineChannelAccessTokenRequest,
) => {
  const response = await axios.post<ReissueLineChannelAccessTokenResponse>(
    `/line/channels/${lineChannelId}/access-token`,
    request,
  );
  return response.data;
};

/**
 * 指定したLINEチャンネルのWebhook URLを現在の環境向けに再登録し、検証する。
 *
 * @param {number} lineChannelId 検証・修復対象のLINEチャンネルID
 * @returns {Promise<TLineWebhookVerifyResponse>} Webhook検証・修復結果
 */
export const verifyLineChannelWebhook = async (lineChannelId: number) => {
  const response = await axios.post<TLineWebhookVerifyResponse>(
    `/line/channels/${lineChannelId}/webhook/verify`,
  );
  return response.data;
};

/**
 * LINEチャンネル情報の更新
 */
export const updateLineChannel = async (
  lineChannelId: number,
  request: TUpdateLineChannelRequest,
) => {
  const response = await axios.put<TLineChannelOperationResponse>(
    `/line/channels/${lineChannelId}`,
    request,
  );
  return response.data;
};

/**
 * LINEチャンネル連携を解除する
 */
export const unlinkLineChannel = async (lineChannelId: number) => {
  const response = await axios.post<boolean>(
    `/line/channels/${lineChannelId}/unlink`,
  );
  return response.data;
};

/**
  * LINE友だち詳細取得
  */
export const getLineFriend = async (userLineId: number) => {
  const response = await axios.get<TLineFriendDetail>(
    `/line/friends/${userLineId}`,
  );
  return response.data;
};

/**
 * 移行メッセージ一括送信
 */
export const sendMigrationMessage = async (
  lineChannelId: number,
  organizationId: number | undefined,
  clientMessageId: string,
) => {
  const response = await axios.post<TSendMigrationMessageResponse>(
    `/line/channels/${lineChannelId}/send-migration-message${orgQuery(organizationId)}`,
    { clientMessageId },
  );
  return response.data;
};

/**
 * LINE友だち情報更新
 */
export const updateLineFriend = async (
  userLineId: number,
  request: TUpdateLineFriendRequest,
) => {
  const response = await axios.put<boolean>(
    `/line/friends/${userLineId}`,
    request,
  );
  return response.data;
};

/**
 * LINE友だちテストユーザー設定更新
 */
export const updateLineFriendTestUser = async (
  userLineId: number,
  isTestUser: boolean,
) => {
  const response = await axios.patch<boolean>(
    `/line/friends/${userLineId}/test-user`,
    { isTestUser },
  );
  return response.data;
};

/**
 * クローリング開始
 */

export const startCrawl = async (body: { url: string; loginId: string; loginPass: string }, organizationId?: number) => {
  const response = await axios.post<TCrawlStartResponse>(`/line/crawl/start${orgQuery(organizationId)}`, body);
  return response.data;
};

export const fetchCrawlDetails = async (jobId: number, organizationId?: number) => {
  const response = await axios.post<TCrawlProgressResponse>(`/line/crawl/${jobId}/fetch-details${orgQuery(organizationId)}`);
  return response.data;
};

export const migrateCrawled = async (jobId: number, organizationId?: number) => {
  const response = await axios.post<TCrawlMigrateResponse>(`/line/crawl/${jobId}/migrate${orgQuery(organizationId)}`);
  return response.data;
};

export const getCrawlResults = async (jobId: number, organizationId?: number) => {
  const response = await axios.get<TCrawlResultItem[]>(`/line/crawl/${jobId}/results${orgQuery(organizationId)}`);
  return response.data;
};

export const migrateFromCsv = async (entries: TMigrateFromCsvEntry[], organizationId?: number) => {
  const response = await axios.post<TCrawlMigrateResponse>(`/line/migrate-from-csv${orgQuery(organizationId)}`, { entries });
  return response.data;
};

export const startChannelCrawl = async (body: { url: string; loginId: string; loginPass: string }, organizationId?: number) => {
  const response = await axios.post<TCrawlStartResponse>(`/line/channel-crawl/start${orgQuery(organizationId)}`, body);
  return response.data;
};

export const fetchChannelCrawlDetails = async (jobId: number, organizationId?: number) => {
  const response = await axios.post<TCrawlProgressResponse>(`/line/channel-crawl/${jobId}/fetch-details${orgQuery(organizationId)}`);
  return response.data;
};

export const getChannelCrawlResults = async (jobId: number, organizationId?: number) => {
  const response = await axios.get<TChannelCrawlResult[]>(`/line/channel-crawl/${jobId}/results${orgQuery(organizationId)}`);
  return response.data;
};

export const bulkRegisterChannels = async (entries: TBulkRegisterChannelEntry[], organizationId?: number) => {
  const response = await axios.post<TBulkRegisterChannelsResponse>(`/line/channels/bulk-register${orgQuery(organizationId)}`, { entries });
  return response.data;
};

/**
 * 選択したLINEチャンネルのWebhook URLを現在の環境向けに再登録し、一括検証する
 */
export const bulkVerifyChannels = async (lineChannelIds: number[]) => {
  const response = await axios.post<TBulkChannelOperationResponse>(
    '/line/channels/bulk/verify',
    { lineChannelIds },
  );
  return response.data;
};

/**
 * 選択したLINEチャンネルのステータスを一括変更する
 */
export const bulkUpdateChannelStatus = async (
  lineChannelIds: number[],
  status: ELineChannelStatus,
) => {
  const response = await axios.post<TBulkChannelOperationResponse>(
    '/line/channels/bulk/status',
    { lineChannelIds, status },
  );
  return response.data;
};

/**
 * 選択したLINEチャンネルの友だち上限数を一括変更する
 */
export const bulkUpdateChannelFriendLimit = async (
  lineChannelIds: number[],
  friendLimit: number,
) => {
  const response = await axios.post<TBulkChannelOperationResponse>(
    '/line/channels/bulk/friend-limit',
    { lineChannelIds, friendLimit },
  );
  return response.data;
};

/**
 * LINE Developers 自動連携
 */
export type TDeveloperCrawlChannel = {
  channelId: string
  basicId: string
  name: string
  status: 'registered' | 'skipped' | 'failed'
  error?: string
}

export type TDeveloperCrawlSyncResponse = {
  registered: number
  skipped: number
  failed: number
  channels: TDeveloperCrawlChannel[]
}

export type TOamProvider = {
  providerId: number
  providerName: string
}

export type TOamAccountResult = {
  basicId: string
  name: string
  status: 'configured' | 'failed'
  error?: string
}

export type TOamConfigureResponse = {
  configured: number
  failed: number
  accounts: TOamAccountResult[]
}

export const getOamProviders = async (cookies: string) => {
  const response = await axios.post<TOamProvider[]>(
    `/line/oam-settings/providers`,
    { cookies },
  );
  return response.data;
};

export const configureOamSettings = async (cookies: string, providerId: number) => {
  const response = await axios.post<TOamConfigureResponse>(
    `/line/oam-settings/configure`,
    { cookies, providerId },
  );
  return response.data;
};

export type TDeveloperProvider = { id: string; name: string }

export const getDeveloperCrawlProviders = async (sesCookie: string) => {
  const response = await axios.post<TDeveloperProvider[]>(
    `/line/developer-crawl/providers`,
    { sesCookie },
  );
  return response.data;
};

export const syncFromDevelopers = async (sesCookie: string, providerId: string, organizationId: number) => {
  const response = await axios.post<TDeveloperCrawlSyncResponse>(
    `/line/developer-crawl/sync`,
    { sesCookie, providerId, organizationId },
  );
  return response.data;
};
